import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  IBookingBusinessHours,
  bookingBusinessHoursResponseSchema,
} from '../../Interfaces/IBookingBusinessHours';
import { customFetcher } from '../useFetch';

const QUERY_KEY = 'bookingbusinesshours-config';
const BASE = '/bookingbusinesshours/config';

/** Schreibbare Felder für den sicheren Custom-Endpoint (Zeiten als String). */
const mapPayload = (row: Partial<IBookingBusinessHours>) => ({
  Weekday: row.Weekday,
  FromTime: row.FromTime,
  ToTime: row.ToTime,
  idMeetingType: row.idMeetingType ?? null,
  idLocation: row.idLocation ?? null,
  idUser: row.idUser ?? null,
});

export const useGetBookingHours = () => {
  const {
    data: bookingHours,
    isLoading: isLoadingBookingHours,
    refetch: refetchBookingHours,
  } = useQuery<IBookingBusinessHours[]>({
    queryKey: [QUERY_KEY],
    queryFn: async () => {
      const data = await customFetcher({ endpoint: BASE });
      return bookingBusinessHoursResponseSchema.parse(data)
        .bookingBusinessHours;
    },
    refetchOnWindowFocus: false,
  });

  return {
    bookingHours: bookingHours ?? [],
    isLoadingBookingHours,
    refetchBookingHours,
  };
};

export const useCreateBookingHours = () => {
  const queryClient = useQueryClient();
  const {
    mutateAsync: createBookingHours,
    isPending: isPendingCreateBookingHours,
  } = useMutation({
    mutationFn: (row: Omit<IBookingBusinessHours, 'idBookingBusinessHours'>) =>
      customFetcher({ endpoint: BASE, method: 'POST', body: mapPayload(row) }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });

  return { createBookingHours, isPendingCreateBookingHours };
};

export const useUpdateBookingHours = () => {
  const queryClient = useQueryClient();
  const {
    mutateAsync: updateBookingHours,
    isPending: isPendingUpdateBookingHours,
  } = useMutation({
    mutationFn: (row: IBookingBusinessHours) =>
      customFetcher({
        endpoint: `${BASE}/${row.idBookingBusinessHours}`,
        method: 'PUT',
        body: mapPayload(row),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });

  return { updateBookingHours, isPendingUpdateBookingHours };
};

export const useDeleteBookingHours = () => {
  const queryClient = useQueryClient();
  const {
    mutateAsync: deleteBookingHours,
    isPending: isPendingDeleteBookingHours,
  } = useMutation({
    mutationFn: (idBookingBusinessHours: number) =>
      customFetcher({
        endpoint: `${BASE}/${idBookingBusinessHours}`,
        method: 'DELETE',
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });

  return { deleteBookingHours, isPendingDeleteBookingHours };
};
